import { StackedBar } from '@unovis/ts'
import { useResizeObserver } from '@vueuse/core'
import { computed, nextTick, onMounted, reactive, ref } from 'vue'

/**
 * Near-transparent fill for the invisible hit-layer `<VisStackedBar>` that
 * extends the tooltip trigger across the full row.
 */
export const HIT_LAYER_COLOR = 'rgba(0,0,0,0.001)'

/**
 * Row-wide hover tracking for horizontal bar charts.
 *
 * Pair with an invisible full-row "hit layer" `<VisStackedBar>` so the tooltip
 * trigger fires anywhere along the row, and render `<PrChartRowHairline>`
 * positioned by `hairlineStyle` to draw a horizontal hairline under the cursor.
 */
export function useRowHairline() {
  const chartRef = ref<HTMLElement | null>(null)
  const hairlineY = ref(0)
  const isHairlineVisible = ref(false)
  const plot = reactive({ top: 0, right: 0, bottom: 0, left: 0 })
  const isMeasured = ref(false)

  function setRef<T>(target: { value: T }, next: T) {
    if (target.value !== next) {
      target.value = next
    }
  }

  function onMouseMove(event: MouseEvent) {
    const el = chartRef.value
    if (!el) {
      return
    }
    if (!isMeasured.value) {
      measurePlotBounds()
    }
    const bounds = el.getBoundingClientRect()
    const y = event.clientY - bounds.top
    setRef(hairlineY, clamp(y, plot.top, bounds.height - plot.bottom))
    setRef(isHairlineVisible, isPointerInPlot(event, bounds))
  }

  function onMouseLeave() {
    setRef(isHairlineVisible, false)
  }

  function isPointerInPlot(event: MouseEvent, bounds: DOMRect) {
    const x = event.clientX - bounds.left
    const y = event.clientY - bounds.top
    return x >= plot.left
      && x <= bounds.width - plot.right
      && y >= plot.top
      && y <= bounds.height - plot.bottom
  }

  function measurePlotBounds() {
    const el = chartRef.value
    if (!el) {
      return
    }
    // The invisible hit-layer bar spans the full plot width on every row, so the
    // union of all rendered bar rects gives the exact plot area.
    const bars = Array.from(el.querySelectorAll<SVGRectElement>(`.${StackedBar.selectors.bar}`))
      .map(b => b.getBoundingClientRect())
      .filter(b => b.width > 0 && b.height > 0)
    if (!bars.length) {
      return
    }

    const bounds = el.getBoundingClientRect()
    const union = bars.reduce(
      (acc, b) => ({
        top: Math.min(acc.top, b.top),
        right: Math.max(acc.right, b.right),
        bottom: Math.max(acc.bottom, b.bottom),
        left: Math.min(acc.left, b.left),
      }),
      { top: bars[0]!.top, right: bars[0]!.right, bottom: bars[0]!.bottom, left: bars[0]!.left },
    )

    plot.top = Math.max(0, union.top - bounds.top)
    plot.bottom = Math.max(0, bounds.bottom - union.bottom)
    plot.left = Math.max(0, union.left - bounds.left)
    plot.right = Math.max(0, bounds.right - union.right)
    isMeasured.value = true
  }

  useResizeObserver(chartRef, measurePlotBounds)
  onMounted(() => nextTick(measurePlotBounds))

  const hairlineStyle = computed(() => ({
    top: `${hairlineY.value}px`,
    left: `${plot.left}px`,
    right: `${plot.right}px`,
  }))

  return {
    chartRef,
    isHairlineVisible,
    hairlineStyle,
    onMouseMove,
    onMouseLeave,
  }
}
